import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle2, UtensilsCrossed, TrendingUp, Clock, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STEPS = [
  {
    section: 'plan',
    icon: UtensilsCrossed,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
    title: 'Моят план',
    desc: 'Тук живее твоят AI хранителен план. Разглеждай всеки ден с пълни рецепти, точни макроси и списък за пазаруване.',
  },
  {
    section: 'progress',
    icon: TrendingUp,
    color: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-50 dark:bg-sky-900/30',
    title: 'Проследяване на прогреса',
    desc: 'Записвай седмичното тегло и следи напредъка с интерактивни графики. Системата прогнозира кога ще достигнеш целта.',
  },
  {
    section: 'history',
    icon: Clock,
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/30',
    title: 'История на плановете',
    desc: 'Всички твои предишни генерирани планове се пазят тук. Можеш да отвориш всеки отново или да го изтеглиш като PDF.',
  },
  {
    section: 'cookbook',
    icon: BookOpen,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    title: 'Готварска книга',
    desc: 'Запазвай любимите си рецепти с бутона за отметка в плана. Намери ги тук по всяко време, филтрирани по вид хранене.',
  },
];

export function DashboardTour({ step, onNext, onDone, sidebarRefs }) {
  const [cardPos, setCardPos] = useState(null); // {top, left} for desktop, null for mobile

  useEffect(() => {
    const measure = () => {
      const el = sidebarRefs?.current?.[step];
      if (el && window.innerWidth >= 768) {
        const r = el.getBoundingClientRect();
        setCardPos({ top: r.top + r.height / 2, left: r.right + 18 });
      } else {
        setCardPos(null);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step, sidebarRefs]);

  const current = STEPS[step] || STEPS[0];
  const Icon = current.icon;
  const isLast = step >= STEPS.length - 1;

  const cardInner = (
    <AnimatePresence mode="wait">
      <motion.div
        key={step}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
      >
        {/* Step progress dots */}
        <div className="flex items-center gap-1.5 pt-4 px-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-5 h-1.5 bg-emerald-500'
                  : i < step
                  ? 'w-1.5 h-1.5 bg-emerald-300 dark:bg-emerald-600'
                  : 'w-1.5 h-1.5 bg-gray-200 dark:bg-slate-700'
              }`}
            />
          ))}
          <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-2 tabular-nums">
            {step + 1}/{STEPS.length}
          </span>
        </div>

        <div className="p-5 pt-3">
          <div className="flex items-start gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl ${current.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
              <Icon className={`w-5 h-5 ${current.color}`} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm leading-tight">
                {current.title}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 leading-relaxed">
                {current.desc}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onDone}
              data-testid="tour-skip"
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors px-1 py-1"
            >
              Пропусни
            </button>
            <Button
              onClick={isLast ? onDone : onNext}
              data-testid={isLast ? 'tour-done' : 'tour-next'}
              className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5 text-sm rounded-xl font-semibold"
            >
              {isLast ? (
                <><CheckCircle2 className="w-4 h-4 mr-1.5" />Разбрах!</>
              ) : (
                <>Следваща<ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <>
      {/* Dimmed backdrop */}
      <motion.div
        className="fixed inset-0 z-[55] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ background: 'rgba(0,0,0,0.50)' }}
      />

      {/* ── Desktop card: follows sidebar item position ── */}
      {cardPos !== null && (
        <motion.div
          className="fixed z-[60] w-80 pointer-events-auto"
          data-testid="dashboard-tour-card"
          style={{ translateY: '-50%' }}
          animate={{ top: cardPos.top, left: cardPos.left, opacity: 1 }}
          initial={{ top: cardPos.top, left: cardPos.left - 12, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 260 }}
        >
          {/* Arrow pointing left toward the sidebar */}
          <div
            className="absolute -left-[9px] top-1/2 -translate-y-1/2 w-4 h-4 rotate-45 rounded-sm bg-white dark:bg-slate-900 border-l border-b border-gray-100 dark:border-slate-700/60"
            style={{ boxShadow: '-2px 2px 5px rgba(0,0,0,0.06)' }}
          />
          {/* Card body */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-slate-700/60">
            {cardInner}
          </div>
        </motion.div>
      )}

      {/* ── Mobile card: fixed at bottom center ── */}
      {cardPos === null && (
        <motion.div
          key={`mobile-${step}`}
          className="fixed bottom-6 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm pointer-events-auto"
          data-testid="dashboard-tour-card"
          style={{ translateX: '-50%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.28 }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-slate-700/60">
            {cardInner}
          </div>
        </motion.div>
      )}
    </>
  );
}
